import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

export default function CoachTasksList() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState("desc");
  
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleSortChange = (newSort: string) => {
    setSort(newSort);
    setPage(1);
  };

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setIsLoading(true);
        const res = await api.get('/tasks', {
          params: {
            page,
            search: debouncedSearch,
            sort,
            per_page: 12
          }
        });
        // Laravel paginate response puts items in 'data'
        setTasks(res.data.data || []);
        setTotalPages(res.data.last_page || 1);
      } catch (err) {
        console.error("Failed to fetch curriculum tasks:", err);
        showToast("Error", "Could not fetch tasks from server.", "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTasks();
  }, [page, debouncedSearch, sort]);

  return (
    <div className="space-y-6 sm:space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20 relative z-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tighter">Curriculum Tasks</h2>
          <p className="text-on-surface-variant font-medium text-xs sm:text-sm">Manage curriculum tasks across all courses.</p>
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative w-full md:w-64 lg:w-80 group">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 group-focus-within:text-primary transition-colors">search</span>
            <input 
              type="text"
              placeholder="Search by task title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-surface-container-low text-on-surface border border-outline-variant/10 rounded-2xl py-3.5 pl-12 pr-6 outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium text-xs sm:text-sm"
            />
          </div>
          <button 
            onClick={() => handleSortChange(sort === "desc" ? "asc" : "desc")}
            className="flex items-center gap-2 bg-surface-container-low border border-outline-variant/10 hover:border-primary/30 hover:text-primary px-4 py-3.5 rounded-2xl transition-all"
            title="Sort by Time"
          >
            <span className="material-symbols-outlined text-[18px]">
              {sort === "desc" ? "arrow_downward" : "arrow_upward"}
            </span>
            <span className="text-xs font-bold uppercase tracking-widest hidden sm:inline">
              {sort === "desc" ? "Newest" : "Oldest"}
            </span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 flex flex-col items-center justify-center text-on-surface-variant gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-[10px] font-black uppercase tracking-widest">Loading Tasks...</p>
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-20 text-center bg-surface-container-lowest border border-dashed border-outline-variant/20 rounded-[2rem]">
          <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 mb-4">search_off</span>
          <p className="text-sm font-black text-on-surface-variant uppercase tracking-[0.2em]">No Tasks Found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {tasks.map((task) => (
              <div key={task.id} className="group bg-surface-container-lowest border border-outline-variant/10 rounded-[2rem] p-6 hover:shadow-2xl hover:border-primary/45 transition-all flex flex-col relative overflow-hidden h-full">
                
                <div className="flex items-center justify-between mb-4">
                  <span className="px-2.5 py-1 bg-surface-container-high text-on-surface-variant rounded-md text-[9px] font-black uppercase tracking-widest border border-outline-variant/5">
                    {task.type}
                  </span>
                  <span className="text-[10px] text-primary/80 font-bold uppercase tracking-widest bg-primary/10 px-2 py-1 rounded">
                    {task.points} PTS
                  </span>
                </div>
                
                <div className="mb-6 flex-1 space-y-2">
                  <h3 className="text-lg font-bold text-on-surface leading-tight group-hover:text-primary transition-colors">{task.title}</h3>
                  <p className="text-xs font-bold text-on-surface-variant/80 uppercase tracking-wide">
                    Course: {task.course?.title || "Unknown Course"}
                  </p>
                  {task.topic && (
                    <p className="text-xs text-on-surface-variant">Topic: {task.topic.name}</p>
                  )}
                </div>
                
                <div className="pt-4 border-t border-outline-variant/10 flex flex-col gap-2">
                  <button 
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="w-full px-4 py-3 bg-primary text-white hover:bg-primary/90 hover:scale-[1.02] rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                  >
                    <span className="material-symbols-outlined text-sm">visibility</span>
                    View Task Details
                  </button>
                  <button 
                    onClick={() => navigate(`/coach/courses/${task.courseId}`)}
                    className="w-full px-4 py-3 bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface hover:scale-[1.02] rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-sm">library_books</span>
                    Go to Course
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-8">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-10 h-10 rounded-xl bg-surface-container border border-outline-variant/10 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-high transition-colors"
              >
                <span className="material-symbols-outlined text-sm">chevron_left</span>
              </button>
              <span className="text-xs font-bold text-on-surface-variant uppercase tracking-widest">
                Page {page} of {totalPages}
              </span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-10 h-10 rounded-xl bg-surface-container border border-outline-variant/10 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-high transition-colors"
              >
                <span className="material-symbols-outlined text-sm">chevron_right</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
